import { Card, CardContent, Grid, Box, Button, Stack, CircularProgress } from "@mui/material";
import Tabs from "../tabs";
import { useState, useEffect } from "react";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { APP_ROUTES } from "../../../../config/appRoutes";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addDepartmentThunk, departmentThunk } from "../../../../components/general/generalThunk";
import { FieldTextField } from "../../../../components/selections/fieldInputs";
import { stepCardSx, stepActionsSx, nextBtnSx, backBtnSx, StepHeader } from "../../../campaign/components/stepUi";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DomainAddOutlinedIcon from "@mui/icons-material/DomainAddOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

// Create-department form. `embedded` = inside the list page popup (no page header, Cancel button,
// onSuccess(message) closes the popup and the list shows the message).
export function DepartmentCreateForm({ embedded = false, onCancel, onSuccess }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { status, error } = useSelector((state) => state.general);

    const [state, setState] = useState({
        departmentName: "",
        open: false,
        message: "",
        severity: "success",
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!state.departmentName.trim()) {
            setState((prev) => ({
                ...prev,
                open: true,
                message: "Department Name is required",
                severity: "error",
            }));
            return;
        }

        try {
             const result = await dispatch(
                addDepartmentThunk({ credentials: { department_name: state.departmentName.trim() } }) // trim before submit
            );

            if (result.meta.requestStatus === "fulfilled") {
                if (onSuccess) {
                    dispatch(departmentThunk({ type: "getDepartment" }));
                    onSuccess(result.payload?.message);
                    return;
                }
                setState({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                    departmentName: "",
                });
                dispatch(departmentThunk({ type: "getDepartment" }));
                navigate(APP_ROUTES.DEPARTMENT.LIST);
            } else {
                setState((prev) => ({
                    ...prev,
                    open: true,
                    message: result.payload,
                    severity: "error",
                }));
            }
        } catch {
            setState((prev) => ({
                ...prev,
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            }));
        }
    };

    useEffect(() => {
        if (error !== null) {
            setState((prev) => ({
                ...prev,
                open: true,
                message: error,
                severity: "error",
            }));
        }
    }, [error]);

    const Wrap = embedded ? Box : Tabs;

    return (
        <div>
            <Wrap>
                <Box mt={embedded ? 0 : 1}>
                    <Card elevation={0} sx={embedded ? { borderRadius: 0, boxShadow: "none" } : stepCardSx}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            {!embedded && (
                                <StepHeader
                                    icon={<DomainAddOutlinedIcon sx={{ fontSize: 20 }} />}
                                    title="Create Department"
                                    subtitle="Add a new department"
                                />
                            )}
                            <Grid spacing={2} container>
                                <Grid item size={embedded ? { xs: 12 } : { xs: 12, sm: 8, md: 5 }}>
                                    <FieldTextField
                                        name="departmentName"
                                        label="Department Name"
                                        placeholder="Enter department name"
                                        required
                                        icon={<BusinessOutlinedIcon />}
                                        value={state.departmentName}
                                        maxLength={20}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.startsWith(" ")) return; // prevent leading space
                                            setState((prev) => ({
                                                ...prev,
                                                departmentName: value,
                                                message: "",
                                            }));
                                        }}
                                        error={
                                            state.severity === "error" &&
                                            state.message === "Department Name is required"
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>

                        {/* Footer actions */}
                        <Box sx={stepActionsSx}>
                            <Stack direction="row" spacing={1.5}>
                                {embedded && (
                                    <Button onClick={onCancel} sx={backBtnSx}>Cancel</Button>
                                )}
                                <Button
                                    disabled={status === "loading"}
                                    onClick={status === "loading" ? null : handleSubmit}
                                    startIcon={status === "loading" ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AddCircleOutlineIcon />}
                                    sx={nextBtnSx}
                                >
                                    {status === "loading" ? "Submitting.." : "Create Department"}
                                </Button>
                            </Stack>
                        </Box>
                    </Card>
                </Box>
            </Wrap>

            <SnackbarMessage
                open={state.open}
                message={state.message}
                severity={state.severity}
                onClose={() => setState((prev) => ({ ...prev, open: false }))}
            />
        </div>
    );
}

// /department-creation page
export default function DepartmentCreation() {
    return <DepartmentCreateForm />;
}
